import { track } from "@amplitude/analytics-react-native";
import { useApolloClient } from "@apollo/client";
import { AlertButton } from "react-native";
import DeletePhotosDocument from "src/api/graphql/__generated__/documents/DeletePhotosDocument";
import PhotoState from "src/api/graphql/__generated__/enums/PhotoState";
import { GatheringPhotoWithBlurbFragmentFragment } from "src/api/graphql/__generated__/graphql";
import evictPhotos from "src/api/graphql/evictPhotos";
import PillButton from "src/components/PillButton";
import getPhotoInfoBlurb from "src/helpers/photoHelpers/getPhotoInfoBlurb";
import IconFilledInfo from "src/icons/IconFilledInfo";
import { alert, Pill } from "src/swsh-native";
import { PillProps } from "src/swsh-native/Pill";

const getTestIdFromState = (state: PhotoState | null | undefined) => {
	switch (state) {
		case PhotoState.Hidden:
			return "Hidden";
		case PhotoState.Preparing:
			return "Preparing";
		case PhotoState.Errored:
			return "Errored";
		default:
			return undefined;
	}
};

const parsePillOptions = (
	photo: GatheringPhotoWithBlurbFragmentFragment,
	pillProps: Partial<PillProps>,
) => {
	const colorVariant = photo.state === PhotoState.Errored ? "Destructive" : "Default";
	return {
		...pillProps,
		colorVariant,
		// Blur isn't compatible with non-default color variants
		blur: colorVariant !== "Default" ? false : (pillProps.blur ?? false),
	};
};
const PhotoInfoBlurbDisplay = ({
	photo,
	...pillProps
}: { photo: GatheringPhotoWithBlurbFragmentFragment } & Pick<
	PillProps,
	"blur" | "sizeVariant"
>) => {
	const photoInfoBlurb = getPhotoInfoBlurb(photo);
	const apolloClient = useApolloClient();
	const colorVariant = photo.state === PhotoState.Errored ? "Destructive" : "Default";

	if (!photoInfoBlurb) return null;
	if (photoInfoBlurb.hasClickAction) {
		return (
			<PillButton
				{...parsePillOptions(photo, pillProps)}
				colorVariant={colorVariant}
				testID={getTestIdFromState(photo.state)}
				Icon={IconFilledInfo}
				onPress={() => {
					const buttons: AlertButton[] = [
						{
							isPreferred: true,
							text: "Got it",
						},
					];
					if (photoInfoBlurb.hasDelete) {
						buttons.unshift({
							onPress: async () => {
								track("photo_infoBlurb_delete");
								await apolloClient.mutate({
									mutation: DeletePhotosDocument,
									variables: {
										input: {
											photoIds: [photo.photoId],
										},
									},
								});
								evictPhotos(apolloClient.cache, [photo.photoId], {
									delay: 300,
								});
							},
							style: "destructive",
							text: "Delete",
						});
					}
					alert({
						title: photoInfoBlurb.title,
						message: photoInfoBlurb.message,
						buttons,
					});
				}}
			>
				{photoInfoBlurb.title}
			</PillButton>
		);
	} else {
		return (
			<Pill
				{...parsePillOptions(photo, pillProps)}
				colorVariant={colorVariant}
				testID={getTestIdFromState(photo.state)}
			>
				{photoInfoBlurb.title}
			</Pill>
		);
	}
};
export default PhotoInfoBlurbDisplay;
